import { roomDisplaysApi } from "@/modules/room-displays/presentation/api/client";
import {
  ROOM_DISPLAY_CHUNK_THRESHOLD_BYTES,
  ROOM_DISPLAY_VIDEO_MAX_BYTES,
  ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS,
} from "@/modules/room-displays/domain/lib/room-display-media";
import { DEFAULT_CHUNK_SIZE } from "@/shared/lib/chunk-upload-s3";

export type RoomDisplayVideoUploadProgress = {
  loaded: number;
  total: number;
};

export function probeVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const objectUrl = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        reject(new Error("ບໍ່ສາມາດອ່ານຄວາມຍາວວິດີໂອໄດ້"));
        return;
      }
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("ບໍ່ສາມາດອ່ານວິດີໂອໄດ້"));
    };
    video.src = objectUrl;
  });
}

export function validateRoomDisplayVideoFile(file: File, durationSec: number) {
  if (file.size <= 0 || file.size > ROOM_DISPLAY_VIDEO_MAX_BYTES) {
    throw new Error("ວິດີໂອຕ້ອງບໍ່ເກີນ 50MB");
  }
  const mime = file.type.trim().toLowerCase();
  if (mime !== "video/mp4" && mime !== "video/webm") {
    throw new Error("ຮອງຮັບ MP4 ແລະ WebM ເທົ່ານັ້ນ");
  }
  if (
    durationSec <= 0 ||
    durationSec > ROOM_DISPLAY_VIDEO_MAX_DURATION_SECONDS
  ) {
    throw new Error("ວິດີໂອຕ້ອງບໍ່ເກີນ 30 ວິນາທີ");
  }
}

async function uploadVideoViaMultipart(
  screenId: string,
  file: File,
  videoDurationSec: number,
  onProgress?: (progress: RoomDisplayVideoUploadProgress) => void,
): Promise<string> {
  const chunkSize = DEFAULT_CHUNK_SIZE;
  const totalSize = file.size;
  const partCount = Math.ceil(totalSize / chunkSize);
  const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);

  const { uploadId, key } = await roomDisplaysApi.initVideoMultipart(screenId, {
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
    videoDurationSec,
  });

  try {
    const { parts: presignedParts } =
      await roomDisplaysApi.presignVideoMultipartParts(screenId, {
        key,
        uploadId,
        partNumbers,
      });

    const completedParts: { partNumber: number; etag: string }[] = [];
    let loaded = 0;

    for (let i = 0; i < partCount; i++) {
      const partNumber = partNumbers[i]!;
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, totalSize);
      const blob = file.slice(start, end);
      const partInfo = presignedParts.find((p) => p.partNumber === partNumber);
      if (!partInfo) {
        throw new Error(`Missing presigned URL for part ${partNumber}`);
      }

      const res = await fetch(partInfo.uploadUrl, {
        method: "PUT",
        body: blob,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          `Part ${partNumber} upload failed: ${res.status}${text ? ` - ${text}` : ""}`,
        );
      }

      let etag = res.headers.get("ETag") ?? "";
      if (etag && !etag.startsWith('"')) etag = `"${etag}"`;
      completedParts.push({ partNumber, etag });
      loaded = end;
      onProgress?.({ loaded, total: totalSize });
    }

    const result = await roomDisplaysApi.completeVideoMultipart(screenId, {
      key,
      uploadId,
      parts: completedParts,
    });
    return result.key;
  } catch (error) {
    await roomDisplaysApi.abortVideoMultipart(screenId, { key, uploadId }).catch(
      () => {},
    );
    throw error;
  }
}

export async function uploadRoomDisplayVideoFile(
  screenId: string,
  file: File,
  videoDurationSec: number,
  options?: {
    onProgress?: (progress: RoomDisplayVideoUploadProgress) => void;
  },
): Promise<string> {
  validateRoomDisplayVideoFile(file, videoDurationSec);

  if (file.size > ROOM_DISPLAY_CHUNK_THRESHOLD_BYTES) {
    return uploadVideoViaMultipart(
      screenId,
      file,
      videoDurationSec,
      options?.onProgress,
    );
  }

  const { uploadUrl, key } = await roomDisplaysApi.presignVideoUpload(
    screenId,
    {
      filename: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      videoDurationSec,
    },
  );

  options?.onProgress?.({ loaded: 0, total: file.size });
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: file.type ? { "Content-Type": file.type } : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `Video upload failed: ${res.status}${text ? ` - ${text}` : ""}`,
    );
  }
  options?.onProgress?.({ loaded: file.size, total: file.size });
  return key;
}
